// DateStrategyList.tsx

import {
    DateStrategyAssign,
    DateStrategyRequest,
    DeleteDateStrategyRequest,
    EditPricelistResponse,
    DateType,
    SchoolStatusDto,
    SchoolIdRequest,
} from "app/shared/model/EditPricelist";
import * as api from "app/shared/api";
import { DateStrategy } from "app/shared/model/EditProductOffering";
import * as React from "react";
import { useSelector } from "react-redux";
import { Row, Col, Modal, Table, Spin, Switch, Card, Button, Space, DatePicker, Alert, Divider, Tag } from "antd";
import {
    DeleteOutlined,
    EditOutlined,
    InfoCircleOutlined,
    CalendarOutlined,
} from "@ant-design/icons";
import { Loader } from "app/shared/ui/Loader";
import "app/shared/components/Table/Table.css";
import "./DateStrategy.css";
import DateStrategyModal from "./DateStrategyModal";
import { PricingYearId } from "app/shared/model";
import { getPricingYears, getSelectedPricingYear } from "../store/selectors";
import DateHelper, { CompareTypes, ManipulationTypes } from "app/helpers/dateHelper";
import Notification from "app/shared/components/Notification/Notification";
import CustomButton from "app/shared/components/Button/CustomButton";
import { CurrentUser } from "app/shared/model/CurrentUser";
import { getCurrentUser } from "app/shared/currentUser";
import { toIso } from '../domain/date/iso';
import { DivisionId } from 'app/shared/division/getDivisionId';
import { normalizeStrategy } from '../domain/dateStrategy/normalize';
import {cloneDeep} from "app/pages/PricelistNew/components/utils/cloneDeep";
import {strategyKey} from "app/pages/PricelistNew/domain/dateStrategy/key";
import {getAllowedDateStrategyTypeIds, getDateStrategyTypeName, toStrategyOptions} from "../domain/dateStrategy/allowed";
import { switchStrategyType } from "../domain/dateStrategy/switchType";
import {
    getPricingYearWindow,
    shiftPickerDateToEarliestAllowed,
    maxIsoDate,
    minIsoDate,
} from "../domain/pricingYear/window";
import dayjs from "dayjs";
import { validateWorkingWeeksDateStrategy } from "../domain/dateStrategy/workingWeeksStartDate";
import { isUnsavedPricelistOffering } from "../domain/offering/isUnsavedOffering";

type NotificationColor =
    | "success"
    | "error"
    | "warning"
    | "red"
    | "orange"
    | "green"
    | "blue"
    | "info";

type UiLabel = {
    content: string;
    color?: NotificationColor;
    active: boolean;
};

interface DateStrategyListProps {
    dateStrategies: DateStrategyAssign[];
    originalDateStrategies: DateStrategyAssign[];
    selectedProduct?: EditPricelistResponse;
    selectedProductType?: string;
    onPendingChanges: (changes: boolean) => void;
    schoolStatuses?: SchoolStatusDto[];
    schoolIds?: SchoolIdRequest[];
    persistDraftOnly?: boolean;
    onDraftDateStrategiesSaved?: (assignments: DateStrategyAssign[]) => void;
    onDateStrategiesChanged?: (assignments: DateStrategyAssign[]) => void;
    onDateStrategyPersisted?: () => void;
    allowEditNewStrategy?: boolean;
    disableAddNew?: boolean;
    showArchived?: boolean;
    onShowArchivedChange?: (show: boolean) => void;
}

interface StateProps {
    pricingYears: PricingYearId[];
    selectedPricingYear?: number;
    user: CurrentUser;
}

type InternalProps = StateProps & DateStrategyListProps;

type StrategyWindow = {
    startDate: string;
    endDate: string;
};

function DateStrategyListContainer(props: DateStrategyListProps) {
    const pricingYears = useSelector((state: any) => getPricingYears(state));
    const selectedPricingYear = useSelector((state: any) => getSelectedPricingYear(state));
    const user = useSelector((state: any) => getCurrentUser(state));

    return (
        <DateStrategyList
            {...props}
            pricingYears={pricingYears}
            selectedPricingYear={selectedPricingYear}
            user={user}
        />
    );
}

const isCurrentActiveDateStrategy = (ds: DateStrategyAssign, todayIso: string): boolean => {
    if (ds.isArchived || ds.isNew) return false;

    const fromIso = toIso(ds.originalFromDate ?? ds.fromDate) || "";
    if (!fromIso) return false;

    if (DateHelper.CompareDates(fromIso, todayIso, CompareTypes.BiggerThan)) {
        return false;
    }

    const endIso = ds.toDate
        ? toIso(ds.toDate)
        : ds.lastStartDate
          ? toIso(ds.lastStartDate)
          : null;

    if (!endIso) return true;

    return !DateHelper.CompareDates(endIso, todayIso, CompareTypes.SmallerThan);
};

const tryGetIsoDate = (value: any): string | null => {
    if (!value) return null;

    if (typeof value === "string") {
        const candidate = value.slice(0, 10);
        if (/^\d{4}-\d{2}-\d{2}$/.test(candidate)) {
            return candidate;
        }
    }

    try {
        const iso = toIso(value);
        if (iso && /^\d{4}-\d{2}-\d{2}$/.test(iso)) {
            return iso;
        }
    } catch {
    }

    return null;
};

const getWindowFromPeriods = (periods: any[]): StrategyWindow | null => {
    if (!Array.isArray(periods) || periods.length === 0) return null;

    const starts = periods
        .map((p) => tryGetIsoDate(p?.fromDate ?? p?.dateFrom ?? p?.startDate ?? p?.periodStartDate))
        .filter(Boolean) as string[];

    const ends = periods
        .map((p) => tryGetIsoDate(p?.toDate ?? p?.dateTo ?? p?.endDate ?? p?.periodEndDate))
        .filter(Boolean) as string[];

    if (!starts.length && !ends.length) return null;

    const startDate = starts.reduce((acc, cur) => minIsoDate(acc, cur), starts[0] ?? ends[0]);
    const endDate = ends.reduce((acc, cur) => maxIsoDate(acc, cur), ends[0] ?? starts[0]);

    return {
        startDate,
        endDate,
    };
};

const STRATEGY_DISPLAY_ORDER = [
    "Recurrence",
    "Fixed Duration",
    "Specific Start Dates",
    "Offering Dates",
];

const STRATEGY_CARD_DESCRIPTIONS: Record<string, string> = {
    Recurrence: "Repeats on selected calendar day. E.g. starts every Monday.",
    "Fixed Duration": "Fixed start and fixed end date. Must book the full block.",
    "Offering Dates":
        "Flexible start and end within a date range. Can book any length inside the window.",
    "Specific Start Dates": "Must start on fixed date. End date depends on booked duration.",
};

const STRATEGY_CARD_ICONS: Record<string, React.ReactNode> = {
    Recurrence: <CalendarOutlined />,
    "Fixed Duration": <CalendarOutlined />,
    "Offering Dates": <CalendarOutlined />,
    "Specific Start Dates": <CalendarOutlined />,
};

const formatStrategyTypeLabel = (strategyTypeId: number): string => {
    const name = getDateStrategyTypeName(strategyTypeId);
    if (name === "Specific Start Dates") return "Specific Start Date";
    return name;
};

const TABLE_PAGE_SIZE = 5;

const getSelectedProductStrategyWindow = (
    product: any,
    fallbackStartDate: string,
    fallbackEndDate: string
): StrategyWindow => {
    const fromPeriods = getWindowFromPeriods(product?.pricePeriods ?? []);

    const singleStart =
        tryGetIsoDate(
            product?.fromDate ??
            product?.dateFrom ??
            product?.periodStartDate ??
            product?.startDate
        ) ?? null;

    const singleEnd =
        tryGetIsoDate(
            product?.toDate ??
            product?.dateTo ??
            product?.periodEndDate ??
            product?.endDate
        ) ?? null;

    const rawStartDate = fromPeriods?.startDate ?? singleStart ?? fallbackStartDate;
    const rawEndDate = fromPeriods?.endDate ?? singleEnd ?? fallbackEndDate;

    const startDate = maxIsoDate(rawStartDate, fallbackStartDate);
    const endDate = minIsoDate(rawEndDate, fallbackEndDate);

    if (!startDate || !endDate || endDate < startDate) {
        return {
            startDate: fallbackStartDate,
            endDate: fallbackEndDate,
        };
    }

    return { startDate, endDate };
};

function DateStrategyList(props: InternalProps) {
    const [dateStrategiesSource, setDateStrategiesSource] = React.useState<DateStrategyAssign[]>(
        cloneDeep(props.dateStrategies ?? [])
    );
    const [selectedDateStrategy, setSelectedDateStrategy] = React.useState<string>("");
    const [selectedDateFrom, setSelectedDateFrom] = React.useState<string>("");
    const [dateStrategyToUpdateCreate, setDateStrategyToUpdateCreate] = React.useState<
        DateStrategyAssign | undefined
    >(undefined);
    const [showModal, setShowModal] = React.useState<boolean>(false);
    const [createNew, setCreateNew] = React.useState<boolean>(false);
    const [label, setLabel] = React.useState<UiLabel | undefined>(undefined);
    const [strategyLabel, setStrategyLabel] = React.useState<UiLabel | undefined>(undefined);
    const [isSaving, setIsSaving] = React.useState<boolean>(false);
    const [pendingChanges, setPendingChanges] = React.useState<boolean>(false);
    const [originalKey, setOriginalKey] = React.useState<{
        from: DateType;
        to: DateType | null;
        strategyTypeId: number;
    } | null>(null);
    /** fromDate shown when the edit modal was opened; used for start-date lock validation. */
    const [editSessionFromIso, setEditSessionFromIso] = React.useState<string | null>(null);
    const [lockedKeys, setLockedKeys] = React.useState<string[]>([]);

    const pricingYears = props.pricingYears ?? [];
    const selectedPricingYearObj =
        pricingYears.find((py) => py.id === props.selectedPricingYear) ?? pricingYears[0];

    const { allowedStartDate, allowedEndDate } = React.useMemo(
        () => getPricingYearWindow(selectedPricingYearObj),
        [selectedPricingYearObj]
    );

    const strategyWindow = React.useMemo(
        () =>
            getSelectedProductStrategyWindow(
                props.selectedProduct as any,
                allowedStartDate,
                allowedEndDate
            ),
        [props.selectedProduct, allowedStartDate, allowedEndDate]
    );

    const [minDateFrom, setMinDateFrom] = React.useState<string>(allowedStartDate);
    const [showArchivedInternal, setShowArchivedInternal] = React.useState<boolean>(false);
    const showArchived = props.showArchived ?? showArchivedInternal;
    const setShowArchived = React.useCallback(
        (value: boolean) => {
            if (props.onShowArchivedChange) {
                props.onShowArchivedChange(value);
            } else {
                setShowArchivedInternal(value);
            }
        },
        [props.onShowArchivedChange]
    );
    const allowEditNewStrategy = props.allowEditNewStrategy === true;
    const disableAddNew = props.disableAddNew === true;
    const isUnsavedOffering = isUnsavedPricelistOffering(props.selectedProduct);

    const createMinDateFrom = maxIsoDate(minDateFrom, strategyWindow.startDate);
    const createMaxDateFrom =
        strategyWindow.endDate >= createMinDateFrom
            ? strategyWindow.endDate
            : allowedEndDate;

    React.useEffect(() => {
        setMinDateFrom(allowedStartDate);
    }, [allowedStartDate]);

    const recalculateDates = React.useCallback(() => {
        const activeStrategies = (dateStrategiesSource ?? []).filter((ds) => !ds.isArchived);

        if (!activeStrategies.length) {
            setMinDateFrom(allowedStartDate);
            return;
        }

        const datesRaw = activeStrategies
            .map((ds) => (ds.toDate ? ds.toDate : ds.lastStartDate ? ds.lastStartDate : ds.fromDate))
            .filter((d) => d != null && d !== "");

        if (!datesRaw.length) {
            setMinDateFrom(allowedStartDate);
            return;
        }

        const validDates: Date[] = [];
        for (const d of datesRaw) {
            try {
                const parsed = DateHelper.ToDate(d);
                if (parsed && !isNaN(parsed.getTime())) validDates.push(parsed);
            } catch {
                // ignore
            }
        }

        if (!validDates.length) {
            setMinDateFrom(allowedStartDate);
            return;
        }

        const max = validDates.reduce((a, b) => (a.getTime() > b.getTime() ? a : b));
        max.setDate(max.getDate() + 1);

        const candidate = DateHelper.ToDateString(max, DateHelper.YYYYMMDD);
        setMinDateFrom(candidate > allowedStartDate ? candidate : allowedStartDate);
    }, [dateStrategiesSource, allowedStartDate]);

    React.useEffect(() => {
        recalculateDates();
    }, [recalculateDates]);

    React.useEffect(() => {
        const next = cloneDeep(props.dateStrategies ?? []);
        setDateStrategiesSource(next);

        const anyArchived = next.some((x: DateStrategyAssign) => x?.isArchived === true);
        const anyActive = next.some((x: DateStrategyAssign) => x?.isArchived !== true);

        setShowArchived(anyArchived && !anyActive);
    }, [props.dateStrategies]);

    const addNewDateStrategy = () => {
        if (
            DateHelper.CompareDates(createMinDateFrom, createMaxDateFrom, CompareTypes.BiggerThan)
        ) {
            setStrategyLabel({
                content: `No available dates left within '${strategyWindow.startDate}' and '${strategyWindow.endDate}'`,
                color: "orange",
                active: true,
            });
            return;
        }

        if (!selectedDateStrategy || !selectedDateFrom || isNaN(Date.parse(selectedDateFrom))) {
            setStrategyLabel({
                content: `'Strategy' and 'Date' cannot be empty`,
                color: "orange",
                active: true,
            });
            return;
        }

        if (selectedDateFrom < createMinDateFrom || selectedDateFrom > createMaxDateFrom) {
            setStrategyLabel({
                content: `Date should be between '${createMinDateFrom}' and '${createMaxDateFrom}'`,
                color: "orange",
                active: true,
            });
            return;
        }

        const strategyTypeId =
            DateStrategy[selectedDateStrategy as keyof typeof DateStrategy] as unknown as number;

        const newDateStrategy: DateStrategyAssign = {
            strategyTypeId,
            fromDate: selectedDateFrom,
            toDate: null,
            fixedDurationDates: [],
            recurranceStartDays: [],
            specificStartDates: [],
            originalFromDate: selectedDateFrom,
            originalToDate: null,
            isNew: true,
            isArchived: false,
        };

        setOriginalKey({
            from: newDateStrategy.originalFromDate,
            to: newDateStrategy.originalToDate ?? null,
            strategyTypeId,
        });
        setDateStrategyToUpdateCreate(cloneDeep(newDateStrategy));
        setCreateNew(true);
        setShowModal(true);
        setStrategyLabel(undefined);
    };

    const disableSaveButton = () => {
        const ds = dateStrategyToUpdateCreate;
        if (!ds) return false;
        if (ds.strategyTypeId === DateStrategy["Offering Dates"]) return false;
        return pendingChanges !== true;
    };

    const pendingChangesHandler = (changes: boolean) => {
        setPendingChanges(changes === true);
        props.onPendingChanges(changes === true);
    };

    const schoolIdsForApi = React.useCallback((): { id: number }[] => {
        // Target every school that has an offering for this product (active AND decommissioned),
        // so date-strategy changes stay in sync across the whole group. A decommissioned school
        // excluded here diverges and, once re-activated, is missing the group's strategies.
        // In schoolStatuses, "Disabled" means the school has no offering, so it is excluded.
        const schoolIdsFromStatuses = Array.isArray(props.schoolStatuses)
            ? props.schoolStatuses
                .filter((s: any) => s?.status && s.status !== "Disabled")
                .map((s: any) => ({ id: Number((s as any)?.schoolId?.id ?? s?.schoolId) }))
                .filter((s: any) => Number.isFinite(s.id))
            : [];

        if (schoolIdsFromStatuses.length > 0) {
            return schoolIdsFromStatuses;
        }

        return Array.isArray(props.schoolIds)
            ? props.schoolIds
                .map((s: any) => ({ id: Number((s as any)?.id ?? s) }))
                .filter((s: any) => Number.isFinite(s.id))
            : [];
    }, [props.schoolIds, props.schoolStatuses]);

    const emitAssignmentsChange = React.useCallback(
        (next: DateStrategyAssign[]) => {
            props.onDateStrategiesChanged?.(next);
            if (props.persistDraftOnly) {
                props.onDraftDateStrategiesSaved?.(next);
            }
        },
        [props.onDateStrategiesChanged, props.persistDraftOnly, props.onDraftDateStrategiesSaved]
    );

    const removeDateStrategyFromState = React.useCallback(
        (ds: DateStrategyAssign) => {
            const key = strategyKey(ds);
            let nextAssignments: DateStrategyAssign[] | null = null;
            setDateStrategiesSource((prev) => {
                nextAssignments = prev.filter((x) => strategyKey(x) !== key);
                return nextAssignments;
            });
            if (nextAssignments) {
                emitAssignmentsChange(nextAssignments);
            }
            setLockedKeys((prev) => prev.filter((k) => k !== key));
            recalculateDates();
        },
        [emitAssignmentsChange, recalculateDates]
    );

    const performDeleteDateStrategy = React.useCallback(
        (ds: DateStrategyAssign, wasEditingThis: boolean): Promise<void> => {
            const pricingYear = selectedPricingYearObj ?? pricingYears[0];
            const product = props.selectedProduct;
            if (!product || !pricingYear) {
                setLabel({ content: "Cannot delete: missing product or pricing year.", color: "red", active: true });
                return Promise.resolve();
            }

            const todayIso = DateHelper.ToDateString(new Date(), DateHelper.YYYYMMDD);
            if (isCurrentActiveDateStrategy(ds, todayIso)) {
                return Promise.resolve();
            }

            const afterRemove = (message: string, ok: boolean) => {
                removeDateStrategyFromState(ds);
                if (wasEditingThis) {
                    setShowModal(false);
                    setDateStrategyToUpdateCreate(undefined);
                    setOriginalKey(null);
                    setEditSessionFromIso(null);
                    setCreateNew(false);
                    setPendingChanges(false);
                    props.onPendingChanges(false);
                }
                setLabel({ content: message, color: ok ? "green" : "red", active: true });
            };

            if (props.persistDraftOnly || ds.isNew) {
                afterRemove(props.persistDraftOnly ? "Removed" : "Removed", true);
                return Promise.resolve();
            }

            const schoolIds = schoolIdsForApi();
            if (schoolIds.length === 0) {
                setLabel({ content: "Select at least one school.", color: "red", active: true });
                return Promise.resolve();
            }

            const originalFromDate = toIso(ds.originalFromDate ?? ds.fromDate) || "";
            const toRaw = ds.originalToDate ?? ds.toDate;
            const originalToDate =
                toRaw != null && toRaw !== "" ? toIso(toRaw) : null;

            const request: DeleteDateStrategyRequest = {
                type: product.type,
                schoolIds,
                productIdText: product.productIdText,
                pricingYear: pricingYear.year,
                dateStrategy: {
                    strategyTypeId: ds.strategyTypeId,
                    originalFromDate,
                    originalToDate,
                },
            };

            setIsSaving(true);
            return api
                .DeleteDateStrategies(request, DivisionId())
                .then((result: any) => {
                    const ok = result?.ok === true;
                    if (ok) {
                        afterRemove(result?.message ?? "Date strategy deleted successfully", true);
                    } else {
                        setLabel({
                            content: result?.message ?? "Failed to delete",
                            color: "red",
                            active: true,
                        });
                    }
                })
                .finally(() => {
                    setIsSaving(false);
                });
        },
        [
            props.selectedProduct,
            props.persistDraftOnly,
            props.onPendingChanges,
            selectedPricingYearObj,
            pricingYears,
            schoolIdsForApi,
            removeDateStrategyFromState,
        ]
    );

    const saveDateStrategyChanges = () => {
        const pricingYear = selectedPricingYearObj ?? pricingYears[0];
        const product = props.selectedProduct;

        if (!pricingYear || !product || !dateStrategyToUpdateCreate) return;

        const fromIso = toIso(dateStrategyToUpdateCreate.fromDate) || createMinDateFrom;
        const toIsoValue = toIso(dateStrategyToUpdateCreate.toDate);

        const isUnsavedOffering = isUnsavedPricelistOffering(product);

        if (
            !createNew &&
            originalKey &&
            !isUnsavedOffering &&
            dateStrategyToUpdateCreate.isNew !== true &&
            !props.persistDraftOnly
        ) {
            const todayIso = DateHelper.ToDateString(new Date(), DateHelper.YYYYMMDD);
            const sessionFromIso =
                editSessionFromIso || toIso(dateStrategyToUpdateCreate.fromDate) || "";

            const strategyAlreadyStarted =
                Boolean(sessionFromIso) &&
                !DateHelper.CompareDates(sessionFromIso, todayIso, CompareTypes.BiggerThan);

            if (strategyAlreadyStarted && fromIso !== sessionFromIso) {
                setLabel({
                    content:
                        "The strategy start date cannot be changed after the strategy has begun (it is on or before today).",
                    color: "red",
                    active: true,
                });
                return;
            }

            if (
                !strategyAlreadyStarted &&
                DateHelper.CompareDates(todayIso, fromIso, CompareTypes.BiggerThan)
            ) {
                setLabel({
                    content: "The strategy start date cannot be before today.",
                    color: "red",
                    active: true,
                });
                return;
            }
        }

        if (toIsoValue) {
            if (DateHelper.CompareDates(toIsoValue, fromIso, CompareTypes.SmallerThan)) {
                setLabel({ content: `Strategy Date To cannot be smaller than Date From`, color: "red", active: true });
                return;
            }
        }

        const effectiveCreateNew =
            createNew || (isUnsavedOffering && dateStrategyToUpdateCreate.isNew === true);

        let strategyForSend: DateStrategyAssign = cloneDeep(dateStrategyToUpdateCreate);
        if (effectiveCreateNew) {
            strategyForSend.isNew = isUnsavedOffering;
            strategyForSend.isArchived = false;
            strategyForSend.originalFromDate = fromIso as any;
            strategyForSend.originalToDate = toIsoValue;
        } else if (originalKey) {
            if (isUnsavedOffering) {
                strategyForSend.originalFromDate = fromIso as any;
                strategyForSend.originalToDate = toIsoValue;
            } else {
                strategyForSend.originalFromDate = (toIso(originalKey.from) || fromIso) as any;
                strategyForSend.originalToDate = originalKey.to;
            }
        }

        if (effectiveCreateNew && (strategyForSend.toDate == null || strategyForSend.toDate === undefined)) {
            if (strategyForSend.strategyTypeId === DateStrategy["Fixed Duration"] && strategyForSend.fixedDurationDates.length > 0) {
                const maxEnd = strategyForSend.fixedDurationDates
                    .slice()
                    .sort((a, b) => DateHelper.DateManipulation(b.startDate, a.startDate, ManipulationTypes.Subtract))[0].endDate;
                strategyForSend.toDate = maxEnd;
            } else if (
                strategyForSend.strategyTypeId === DateStrategy["Specific Start Dates"] &&
                strategyForSend.specificStartDates.length > 0
            ) {
                const max = strategyForSend.specificStartDates
                    .slice()
                    .sort((a, b) => DateHelper.DateManipulation(b.date, a.date, ManipulationTypes.Subtract))[0].date;
                strategyForSend.lastStartDate = max;
            }
        }

        const schoolIds = schoolIdsForApi();

        if (schoolIds.length === 0) {
            setLabel({ content: "Select at least one school.", color: "red", active: true });
            return;
        }

        const workingWeeksError = validateWorkingWeeksDateStrategy(
            strategyForSend,
            (product as any)?.unitTypeId,
            (product as any)?.unitTypeName ?? (product as any)?.unitTypeText
        );
        if (workingWeeksError) {
            setLabel({ content: workingWeeksError, color: "red", active: true });
            return;
        }

        const request: DateStrategyRequest = {
            type: product.type,
            schoolIds,
            productIdText: product.productIdText,
            pricingYear: pricingYear.year,
            dateStrategy: normalizeStrategy(strategyForSend),
            createNew: effectiveCreateNew,
        };

        if ((props.persistDraftOnly || isUnsavedOffering) && request.dateStrategy) {
            setIsSaving(true);
            const ds = request.dateStrategy;
            const persisted: DateStrategyAssign = {
                ...ds,
                isNew: isUnsavedOffering ? true : false,
                originalFromDate: ds.fromDate,
                originalToDate: ds.toDate ?? null,
            };
            const newKey = strategyKey(persisted);
            if (!isUnsavedOffering) {
                setLockedKeys((prev) => (prev.includes(newKey) ? prev : [...prev, newKey]));
            }

            let nextAssignments: DateStrategyAssign[];
            if (createNew) {
                nextAssignments = [...dateStrategiesSource, persisted];
            } else {
                const keyToFind = originalKey
                    ? `${originalKey.strategyTypeId}|${toIso(originalKey.from) || toIso(ds.fromDate)}|${toIso(originalKey.to)}`
                    : strategyKey(ds);
                const copy = dateStrategiesSource.slice();
                const idx = copy.findIndex((x) => strategyKey(x) === keyToFind);
                if (idx >= 0) {
                    copy[idx] = persisted;
                } else if (isUnsavedOffering && copy.length === 1) {
                    copy[0] = persisted;
                }
                nextAssignments = copy;
                setOriginalKey({
                    from: persisted.originalFromDate,
                    to: persisted.originalToDate ?? null,
                    strategyTypeId: persisted.strategyTypeId,
                });
            }

            setDateStrategiesSource(nextAssignments);
            recalculateDates();
            emitAssignmentsChange(nextAssignments);

            setLabel({ content: "Saved", color: "green", active: true });
            setSelectedDateStrategy("");
            setSelectedDateFrom("");
            setPendingChanges(false);
            props.onPendingChanges(false);
            setIsSaving(false);
            setShowModal(false);
            setCreateNew(false);
            setEditSessionFromIso(null);
            return;
        }

        setIsSaving(true);

        api.PutDateStrategies(request, DivisionId()).then((result: any) => {
            const ok = result?.ok === true;

            if (ok && request.dateStrategy) {
                const persisted: DateStrategyAssign = {
                    ...request.dateStrategy,
                    isNew: false,
                    isArchived: false,
                    originalFromDate: request.dateStrategy.fromDate,
                    originalToDate: request.dateStrategy.toDate ?? null,
                };

                const newKey = strategyKey(persisted);
                setLockedKeys((prev) => (prev.includes(newKey) ? prev : [...prev, newKey]));

                let nextAssignments: DateStrategyAssign[];
                if (createNew) {
                    nextAssignments = [...dateStrategiesSource, persisted];
                } else {
                    const keyToFind = originalKey
                        ? `${originalKey.strategyTypeId}|${toIso(originalKey.from)}|${toIso(originalKey.to)}`
                        : strategyKey(request.dateStrategy);

                        const copy = dateStrategiesSource.slice();
                        const idx = copy.findIndex((x) => strategyKey(x) === keyToFind);
                        if (idx >= 0) copy[idx] = persisted;
                        nextAssignments = copy;

                    setOriginalKey({
                        from: persisted.originalFromDate,
                        to: persisted.originalToDate ?? null,
                        strategyTypeId: persisted.strategyTypeId,
                    });
                }

                setDateStrategiesSource(nextAssignments);
                emitAssignmentsChange(nextAssignments);
                recalculateDates();
                props.onDateStrategyPersisted?.();
            }

            setLabel({
                content: result?.message ?? (ok ? "Saved" : "Failed"),
                color: ok ? "green" : "red",
                active: true,
            });

        if (ok) {
            setSelectedDateStrategy("");
            setSelectedDateFrom("");
            setPendingChanges(false);
            props.onPendingChanges(false);
            setShowModal(false);
            setCreateNew(false);
            setEditSessionFromIso(null);
        }
        setIsSaving(false);
        });
    };

    const onCancel = () => {
        setSelectedDateStrategy("");
        setSelectedDateFrom("");
        setDateStrategiesSource(cloneDeep(props.originalDateStrategies ?? []));
        setShowModal(false);
        setPendingChanges(false);
        props.onPendingChanges(false);
        setStrategyLabel(undefined);
        setCreateNew(false);
        setDateStrategyToUpdateCreate(undefined);
        setOriginalKey(null);
        setEditSessionFromIso(null);
    };

    const allowedIds = React.useMemo(
        () => getAllowedDateStrategyTypeIds(props.selectedProductType, props.selectedProduct as any),
        [props.selectedProductType, props.selectedProduct]
    );

    const dateStrategies = React.useMemo(() => {
        const options = toStrategyOptions(allowedIds);
        return options.slice().sort((a, b) => {
            const indexA = STRATEGY_DISPLAY_ORDER.indexOf(a.value);
            const indexB = STRATEGY_DISPLAY_ORDER.indexOf(b.value);
            return (indexA === -1 ? 999 : indexA) - (indexB === -1 ? 999 : indexB);
        });
    }, [allowedIds]);

    const showStrategyChooser = !disableAddNew || (isUnsavedOffering && dateStrategies.length > 1);
    const currentActiveStrategy = React.useMemo(
        () =>
            dateStrategiesSource.find((ds) => ds.isArchived !== true) ??
            dateStrategiesSource[0],
        [dateStrategiesSource]
    );

    const openStrategyEditor = React.useCallback(
        (original: DateStrategyAssign, nextStrategy?: DateStrategyAssign) => {
            const from = toIso(original.originalFromDate)
                ? original.originalFromDate
                : original.fromDate;
            const to = (original.originalToDate ?? original.toDate ?? null) as DateType | null;
            const sessionFromIso =
                toIso(original.fromDate) || toIso(original.originalFromDate) || "";

            setEditSessionFromIso(sessionFromIso);
            setOriginalKey({ from, to, strategyTypeId: original.strategyTypeId });
            setDateStrategyToUpdateCreate(cloneDeep(nextStrategy ?? original));
            setCreateNew(false);
            setShowModal(true);
        },
        []
    );

    const onStrategyCardClick = (optionValue: string) => {
        setSelectedDateStrategy(optionValue);

        if (!disableAddNew || !isUnsavedOffering || !currentActiveStrategy) {
            return;
        }

        const strategyTypeId = DateStrategy[
            optionValue as keyof typeof DateStrategy
        ] as unknown as number;

        const nextStrategy =
            currentActiveStrategy.strategyTypeId === strategyTypeId
                ? currentActiveStrategy
                : switchStrategyType(
                      currentActiveStrategy,
                      strategyTypeId,
                      strategyWindow.endDate
                  );

        openStrategyEditor(currentActiveStrategy, nextStrategy);
    };

    React.useEffect(() => {
        if (showModal) return;

        if (isUnsavedOffering && currentActiveStrategy) {
            const name = getDateStrategyTypeName(currentActiveStrategy.strategyTypeId);
            if (name) {
                setSelectedDateStrategy(name);
                return;
            }
        }

        if (!selectedDateStrategy && dateStrategies.length === 1) {
            setSelectedDateStrategy(dateStrategies[0].value);
        }
    }, [
        showModal,
        dateStrategies,
        selectedDateStrategy,
        isUnsavedOffering,
        currentActiveStrategy,
    ]);

    React.useEffect(() => {
        if (selectedDateStrategy && !dateStrategies.some((o) => o.value === selectedDateStrategy)) {
            setSelectedDateStrategy("");
        }
    }, [dateStrategies, selectedDateStrategy]);

    const columns = [
        {
            title: "",
            dataIndex: "rowIndex",
            render: (_: any, ds: DateStrategyAssign, dsi: number) => {
                const locked = lockedKeys.includes(strategyKey(ds));
                return (
                    <span>
            {dsi + 1}
                        {locked ? (
                            <i className="icon lock" title="Locked due to pending changes" />
                        ) : ds.isNew === true && !allowEditNewStrategy ? (
                            <i className="icon lock" title="Locked until offering is saved" />
                        ) : null}
          </span>
                );
            },
            width: 60,
        },
        {
            title: "Applied From",
            dataIndex: "fromDate",
            render: (val: any) => DateHelper.ToDateString(val),
            width: 130,
        },
        {
            title: "Applied To (Last Start Date)",
            dataIndex: "toDate",
            render: (_: any, row: DateStrategyAssign) =>
                row.toDate
                    ? DateHelper.ToDateString(row.toDate)
                    : row.lastStartDate
                        ? `(${DateHelper.ToDateString(row.lastStartDate)})`
                        : "",
            width: 180,
        },
        {
            title: "Strategy Type",
            dataIndex: "strategyTypeId",
            render: (val: number) => formatStrategyTypeLabel(val),
            width: 140,
        },
        {
            title: "Status",
            dataIndex: "isArchived",
            render: (isArchived: boolean) => (
                <Tag color={isArchived ? "default" : "green"}>
                    {isArchived ? "Archived" : "Active"}
                </Tag>
            ),
            width: 100,
        },
        {
            title: "",
            dataIndex: "action",
            render: (_: any, ds: DateStrategyAssign) => {
                const locked = lockedKeys.includes(strategyKey(ds));
                if (ds.isArchived) {
                    return null;
                }

                const todayIso = DateHelper.ToDateString(new Date(), DateHelper.YYYYMMDD);
                const hideDelete =
                    (ds.isNew && allowEditNewStrategy) ||
                    isCurrentActiveDateStrategy(ds, todayIso);

                return (
                    <Space size={4}>
                        <Button
                            type="text"
                            icon={<EditOutlined />}
                            aria-label="Edit date strategy"
                            disabled={locked || (ds.isNew && !allowEditNewStrategy)}
                            onClick={() => {
                                openStrategyEditor(ds);
                            }}
                            style={{ padding: "4px 8px" }}
                        />
                        {!hideDelete ? (
                        <Button
                            type="text"
                            danger
                            icon={<DeleteOutlined />}
                            aria-label="Delete date strategy"
                            disabled={locked}
                            onClick={() => {
                                Modal.confirm({
                                    title: "Delete date strategy?",
                                    content: "This scheduled strategy will be removed for this product.",
                                    okText: "Delete",
                                    okType: "danger",
                                    cancelText: "Cancel",
                                    onOk: () => {
                                        const wasEditingThis =
                                            showModal &&
                                            !!dateStrategyToUpdateCreate &&
                                            strategyKey(dateStrategyToUpdateCreate) === strategyKey(ds);
                                        return performDeleteDateStrategy(ds, wasEditingThis);
                                    },
                                });
                            }}
                            style={{ padding: "4px 8px" }}
                        />
                        ) : null}
                    </Space>
                );
            },
            width: 120,
        },
    ];

    const dataSource = React.useMemo(() => {
        return dateStrategiesSource
            .slice()
            .filter((ds) => showArchived === true || !ds.isArchived)
            .sort((a, b) => {
                const aArchived = a.isArchived === true;
                const bArchived = b.isArchived === true;
                if (aArchived !== bArchived) {
                    return aArchived ? 1 : -1;
                }

                const fromA = toIso(a.originalFromDate ?? a.fromDate) || "";
                const fromB = toIso(b.originalFromDate ?? b.fromDate) || "";
                return fromA.localeCompare(fromB);
            })
            .map((ds) => ({ ...ds, key: strategyKey(ds) }));
    }, [dateStrategiesSource, showArchived]);

    const canAddDateStrategy = Boolean(selectedDateStrategy && selectedDateFrom);

    const currentStrategyFromIso =
        toIso(dateStrategyToUpdateCreate?.fromDate) ||
        toIso(dateStrategyToUpdateCreate?.originalFromDate) ||
        "";

    const modalMinDateFrom = createNew
        ? minIsoDate(createMinDateFrom, currentStrategyFromIso || createMinDateFrom)
        : isUnsavedOffering
          ? minIsoDate(currentStrategyFromIso || allowedStartDate, allowedStartDate)
          : strategyWindow.startDate;
    const modalMaxDateFrom = isUnsavedOffering || createNew
        ? maxIsoDate(currentStrategyFromIso || allowedEndDate, allowedEndDate)
        : strategyWindow.endDate >= modalMinDateFrom
          ? strategyWindow.endDate
          : allowedEndDate;

    return (
        <Card className="date-strategy-list" styles={{ body: { padding: 0 } }} style={{ border: "none", boxShadow: "none" }}>
            <Spin spinning={isSaving} description={<Loader />}>
                <div className="container">
                    <Alert
                        className="date-strategy-info-banner"
                        type="info"
                        showIcon
                        icon={<InfoCircleOutlined />}
                        title="Date strategy controls which start dates are allowed for the offering, and how the end date is determined."
                    />

                    <Row className="content" gutter={[0, 20]}>
                        {showStrategyChooser ? (
                            <Col span={24}>
                                <div className="date-strategy-choose-panel">
                                    <div className="date-strategy-choose-title">
                                        {disableAddNew ? "Change Strategy" : "Choose Strategy"}
                                    </div>
                                    <div
                                        className="date-strategy-cards"
                                        data-card-count={Math.min(dateStrategies.length, 4)}
                                    >
                                        {dateStrategies.map((option) => {
                                            const isSelected = selectedDateStrategy === option.value;
                                            return (
                                                <div
                                                    key={option.value}
                                                    className={`date-strategy-card${isSelected ? " selected" : ""}`}
                                                    role="button"
                                                    tabIndex={0}
                                                    aria-pressed={isSelected}
                                                    onClick={() => onStrategyCardClick(option.value)}
                                                    onKeyDown={(e) => {
                                                        if (e.key === "Enter" || e.key === " ") {
                                                            e.preventDefault();
                                                            onStrategyCardClick(option.value);
                                                        }
                                                    }}
                                                >
                                                    <div className="date-strategy-card-icon">
                                                        {STRATEGY_CARD_ICONS[option.value]}
                                                    </div>
                                                    <div className="date-strategy-card-title">{option.label}</div>
                                                    <div className="date-strategy-card-description">
                                                        {STRATEGY_CARD_DESCRIPTIONS[option.value]}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>

                                    {!disableAddNew ? (
                                    <div className="date-strategy-applies-action-row">
                                        <div className="date-strategy-applies-field-inline">
                                            <span className="date-strategy-applies-label">Applies From</span>
                                        <DatePicker
                                            format={DateHelper.DATE_PICKER_FORMATS}
                                            placeholder="Select date"
                                            value={selectedDateFrom ? dayjs(selectedDateFrom) : null}
                                            disabledDate={(date) =>
                                                date.isBefore(dayjs(createMinDateFrom), "day") ||
                                                date.isAfter(dayjs(createMaxDateFrom), "day")
                                            }
                                            onChange={(value) => {
                                                if (!value) {
                                                    setSelectedDateFrom("");
                                                    return;
                                                }
                                                setSelectedDateFrom(
                                                    shiftPickerDateToEarliestAllowed(
                                                        value,
                                                        createMinDateFrom,
                                                        createMaxDateFrom
                                                    )
                                                );
                                            }}
                                            style={{ width: 200 }}
                                        />
                                    </div>
                                    <div className="date-strategy-action-col">
                                        {strategyLabel ? (
                                            <Notification
                                                dedupeKey="date-strategy-saved"
                                                pointing="right"
                                                size="small"
                                                {...strategyLabel}
                                                visible={strategyLabel.active}
                                                autoHide
                                                autoHideSeconds={10000}
                                                onHide={() =>
                                                    setStrategyLabel({ ...strategyLabel, active: false })
                                                }
                                            />
                                        ) : null}
                                        <Button
                                            type="primary"
                                            disabled={!canAddDateStrategy}
                                            onClick={addNewDateStrategy}
                                        >
                                            Add Date Strategy
                                        </Button>
                                    </div>
                                </div>
                                    ) : null}
                            </div>
                            </Col>
                        ) : null}

                        <Col span={24}>
                            {label ? (
                                <div style={{ marginBottom: 10 }}>
                                    <Notification
                                        dedupeKey="date-strategy-saved"
                                        pointing="left"
                                        {...label}
                                        visible={label.active}
                                        autoHide
                                        autoHideSeconds={10000}
                                        onHide={() => setLabel({ ...label, active: false })}
                                    />
                                </div>
                            ) : null}

                            <Divider className="date-strategy-section-divider" />
                            <div className="date-strategy-section-title">Current Date Strategies</div>
                            {dateStrategiesSource.some((ds) => ds.isArchived) && (
                                <Space style={{ marginBottom: 10 }}>
                                    <Switch
                                        checked={showArchived}
                                        onChange={setShowArchived}
                                        size="small"
                                    />
                                    <span>Show archived data strategies</span>
                                </Space>
                            )}

                            <Table
                                bordered
                                size="small"
                                dataSource={dataSource}
                                columns={columns as any}
                                rowKey="key"
                                pagination={
                                    dataSource.length > TABLE_PAGE_SIZE
                                        ? { pageSize: TABLE_PAGE_SIZE, showSizeChanger: false }
                                        : false
                                }
                                style={{ margin: "15px 0" }}
                                className="main body date-strategy-table"
                                rowClassName={(ds: any) => {
                                    if (ds?.isArchived) return "archived-strategy-row";
                                    if (ds?.isNew && !allowEditNewStrategy) return "locked";
                                    return "";
                                }}
                            />
                            <p className="note">*Dates in brackets indicate 'Last Start Date' instead of 'To Date'</p>
                        </Col>
                    </Row>
                </div>

                <Modal
                    open={showModal}
                    title={`${createNew ? "Add" : "Update"} Date Strategy for ${
                        props.selectedProduct ? `'${props.selectedProduct.name}'` : ""
                    }`}
                    onCancel={onCancel}
                    footer={[
                        <CustomButton key="cancel" text="Cancel" secondary onClick={onCancel} />,
                        <CustomButton
                            key="save"
                            primary
                            disabled={disableSaveButton()}
                            saving={isSaving}
                            text="Save"
                            savingText="Saving..."
                            onClick={saveDateStrategyChanges}
                        />,
                    ]}
                    width={800}
                    style={{ top: 50 }}
                    mask={{ closable: false }}
                    destroyOnHidden
                >
                    <DateStrategyModal
                        selectedProduct={props.selectedProduct}
                        isUnsavedOffering={isUnsavedOffering}
                        createNew={createNew}
                        dateStrategyToUpdateCreate={dateStrategyToUpdateCreate}
                        minDateFrom={modalMinDateFrom}
                        maxDateFrom={modalMaxDateFrom}
                        allowedStartDate={strategyWindow.startDate}
                        allowedEndDate={strategyWindow.endDate}
                        onPendingChanges={pendingChangesHandler}
                        onDateStrategyUpdate={(ds) => {
                            if (!ds) return;

                            let next = cloneDeep(ds);
                            const keepOriginalIdentity =
                                !createNew &&
                                originalKey &&
                                (props.selectedProduct as any)?.isNew !== true &&
                                next.isNew !== true;
                            if (keepOriginalIdentity) {
                                next.originalFromDate = originalKey.from;
                                next.originalToDate = originalKey.to;
                            }

                            setDateStrategyToUpdateCreate(next);
                        }}
                    />
                </Modal>
            </Spin>
        </Card>
    );
}

export default DateStrategyListContainer;